// src/pages/CartPage.js — Premium cart experience
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart }  from '../context/CartContext';
import { useToast } from '../context/ToastContext';
import { ordersApi, unwrap, ApiError, ErrorCode, getImageUrl, getImageUrlFallback } from '../services/api';
import { Button, EmptyState } from '../components/ui';

const COUPON_MSG = {
  [ErrorCode.COUPON_EXPIRED]:             'This coupon has expired.',
  [ErrorCode.COUPON_LIMIT]:               'This coupon has reached its usage limit.',
  COUPON_EXCLUDED_CATEGORY:               null, // handled separately with detail
};

function couponError(err) {
  if (!(err instanceof ApiError)) return 'Invalid coupon code.';
  if (err.code === 'COUPON_EXCLUDED_CATEGORY') {
    const cats = err.detail?.excluded_categories || [];
    return cats.length
      ? `This coupon is not applicable for ${cats.join(', ')}.`
      : err.message || 'This coupon is not applicable for some items in your cart.';
  }
  if (COUPON_MSG[err.code]) return COUPON_MSG[err.code];
  if (err.code === ErrorCode.COUPON_MIN_ORDER) {
    const min = err.detail?.min_order_value;
    return min ? `Minimum order of ₹${parseFloat(min).toFixed(0)} required.` : err.message;
  }
  return err.message || 'Invalid coupon code.';
}

function CartItem({ item }) {
  const { updateQty, removeItem } = useCart();
  const { toast } = useToast();
  const { product, qty } = item;
  const img = getImageUrl(product, 160);
  const imgFallback = getImageUrlFallback();

  return (
    <div className="flex gap-2 sm:gap-4 items-start sm:items-center py-4 sm:py-5 border-b border-gray-100/80 last:border-0 group animate-fade-in">
      <Link to={`/products/${product.slug}`} className="flex-shrink-0">
        <img src={img} alt={product.name}
        loading="lazy" decoding="async" width="64" height="64"
          onError={e=>{e.target.src=imgFallback;}}
          className="w-16 h-16 sm:w-20 sm:h-20 object-cover rounded-2xl bg-amber-50
            group-hover:shadow-md transition-shadow duration-200"/>
      </Link>
      <div className="flex-1 min-w-0">
        <Link to={`/products/${product.slug}`}
          className="font-bold text-gray-800 text-sm hover:text-red-700 transition-colors line-clamp-2 block mb-1">
          {product.name}
        </Link>
        <p className="text-xs text-gray-400 mb-1.5 flex items-center gap-1.5">
          {product.category_name}
          {product.unit_type && (
            <span className="bg-amber-100 text-amber-700 text-[10px] font-black
              px-1.5 py-0.5 rounded uppercase tracking-wide">
              {{'box':'Box','pkt':'Pkt','piece':'Piece','set':'Set','dozen':'Dozen','bundle':'Bundle','roll':'Roll'}[product.unit_type] || product.unit_type}
            </span>
          )}
        </p>
        <p className="font-black text-sm text-gray-900" style={{fontFamily:'"Plus Jakarta Sans",sans-serif'}}>
          ₹{product.price}
        </p>
      </div>
      <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl overflow-hidden flex-shrink-0">
        <button onClick={()=>updateQty(product.id,qty-1)}
          className="px-2 sm:px-3 py-2 hover:bg-red-50 hover:text-red-600 text-gray-600 font-bold transition-colors text-sm">−</button>
        <span className="px-2 sm:px-4 py-2 font-black text-gray-800 min-w-[1.75rem] sm:min-w-[2.5rem] text-center text-sm">{qty}</span>
        <button onClick={()=>updateQty(product.id,qty+1)} disabled={qty>=250}
          className="px-2 sm:px-3 py-2 hover:bg-red-50 hover:text-red-600 text-gray-600 font-bold transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-sm">+</button>
      </div>
      <div className="hidden sm:block text-right w-20 flex-shrink-0">
        <p className="font-black text-gray-900" style={{fontFamily:'"Plus Jakarta Sans",sans-serif'}}>
          ₹{(parseFloat(product.price)*qty).toFixed(0)}
        </p>
      </div>
      <button onClick={()=>{removeItem(product.id);toast.info(`"${product.name}" removed.`);}}
        className="text-gray-300 hover:text-red-500 transition-all p-1.5 rounded-lg hover:bg-red-50 flex-shrink-0"
        aria-label="Remove item">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    </div>
  );
}

function CouponInput() {
  const { coupon, setCoupon, clearCoupon, subtotal, discountAmount, couponInvalid,
          couponExcluded, excludedItems, items } = useCart();
  const { toast }  = useToast();
  const [code,    setCode]    = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  if (coupon) return (
    <div className="space-y-2 animate-fade-in">
      <div className={`flex items-center justify-between rounded-2xl px-4 py-3.5
        ${couponExcluded
          ? 'bg-red-50 border border-red-200'
          : couponInvalid
          ? 'bg-amber-50 border border-amber-200'
          : 'bg-green-50 border border-green-200/60'
        }`}>
        <div className="flex-1 min-w-0">
          <p className={`font-black text-sm flex items-center gap-2
            ${couponExcluded ? 'text-red-700' : couponInvalid ? 'text-amber-700' : 'text-green-700'}`}>
            <span>{couponExcluded ? '🚫' : couponInvalid ? '⚠' : '✓'}</span>
            Coupon "{coupon.code}" {couponExcluded ? 'blocked' : 'applied'}
          </p>
          {couponExcluded ? (
            <div className="mt-1">
              <p className="text-red-600 text-xs font-semibold">
                This coupon is not applicable for:
              </p>
              <ul className="mt-0.5 space-y-0.5">
                {excludedItems.map((item, i) => (
                  <li key={i} className="text-red-500 text-xs flex items-center gap-1">
                    <span>•</span> {item.name}
                    <span className="text-red-400">({item.category_name})</span>
                  </li>
                ))}
              </ul>
              <p className="text-red-500 text-xs mt-1 font-medium">
                This coupon is not applicable for Combo Packs and Gift Boxes.
              </p>
            </div>
          ) : couponInvalid ? (
            <p className="text-amber-600 text-xs mt-0.5">
              Cart total is below ₹{parseFloat(coupon.min_order_value || 0).toFixed(0)} minimum — discount paused.
            </p>
          ) : (
            <p className="text-green-600 text-xs mt-0.5">
              You're saving ₹{discountAmount.toFixed(0)} on this order!
            </p>
          )}
        </div>
        <button onClick={()=>{clearCoupon();toast.info('Coupon removed.');}}
          className="text-red-400 hover:text-red-600 text-xs font-bold transition-colors px-2 py-1 rounded-lg hover:bg-red-50 flex-shrink-0 ml-2">
          Remove
        </button>
      </div>
    </div>
  );

  const apply = async () => {
    const trimmed = code.trim().toUpperCase();
    if (!trimmed) return;

    // Client-side pre-check: warn if excluded items present
    const excludedSlugs = ['gift-boxes', 'combo-packs'];
    const hasExcluded = items.some(i =>
      excludedSlugs.includes((i.product.category_slug || '').toLowerCase())
    );
    if (hasExcluded) {
      const blocked = items
        .filter(i => excludedSlugs.includes((i.product.category_slug || '').toLowerCase()))
        .map(i => i.product.category_name);
      setError(`This coupon is not applicable for ${[...new Set(blocked)].join(', ')}.`);
      return;
    }

    setLoading(true); setError('');
    try {
      // Send product_ids so backend can verify excluded categories server-side too
      const productIds = items.map(i => i.product.id);
      const res  = await ordersApi.validateCoupon({ code: trimmed, subtotal, product_ids: productIds });
      const data = unwrap(res);
      setCoupon(data);
      toast.success(`🎉 Coupon applied! You save ₹${parseFloat(data.discount).toFixed(0)}`);
      setCode('');
    } catch (err) {
      setError(couponError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex gap-2">
        <input value={code} onChange={e=>{setCode(e.target.value.toUpperCase());setError('');}}
          onKeyDown={e=>e.key==='Enter'&&apply()}
          placeholder="COUPON CODE"
          className="flex-1 bg-gray-50 border border-gray-200 rounded-xl py-3 px-4 text-sm
            font-mono tracking-widest text-gray-800 placeholder-gray-300
            outline-none focus:bg-white focus:ring-2 focus:ring-yellow-400/30 focus:border-yellow-500
            transition-all"
          maxLength={20}/>
        <Button onClick={apply} loading={loading} disabled={!code.trim()} size="md">Apply</Button>
      </div>
      {error && <p className="text-red-600 text-xs mt-2 font-medium animate-fade-in flex items-center gap-1"><span>⚠</span>{error}</p>}
    </div>
  );
}

export default function CartPage() {
  // ── SEO: update page title and meta description ───────────────
  React.useEffect(() => {
    document.title = 'Your Cart — Lakshmi Crackers';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', 'Review your selected crackers and place your order. Lakshmi Crackers — Quality fireworks from Sivakasi.');
    return () => {
      document.title = 'Lakshmi Crackers 🪔 — Premium Fireworks';
    };
  }, []);

  const navigate = useNavigate();
  const { items, coupon, subtotal, discountAmount, couponInvalid, couponExcluded, total, itemCount, clearCart } = useCart();
  const MIN_ORDER_VALUE = 2500;
  const isBelowMinOrder = subtotal < MIN_ORDER_VALUE;
  const { toast } = useToast();

  if (!items.length) return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{background:'#fffaf0'}}>
      <EmptyState icon="🛒" title="Your cart is empty" message="Add some crackers to get started!"
        action={
          <Link to="/products"
            className="inline-block bg-gradient-to-br from-red-600 to-red-800 text-white
              font-black px-10 py-4 rounded-2xl shadow-xl shadow-red-600/30
              hover:shadow-red-600/50 hover:-translate-y-1 transition-all duration-200 mt-2">
            🎆 Browse Products
          </Link>
        }/>
    </div>
  );

  return (
    <div className="min-h-screen py-12" style={{background:'#fffaf0'}}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between mb-10 animate-fade-up">
          <div>
            <h1 className="font-black text-gray-900" style={{fontFamily:'"Playfair Display",serif',fontSize:'2.25rem'}}>
              Shopping Cart
            </h1>
            <p className="text-gray-500 text-sm mt-1">
              {itemCount} {itemCount===1?'item':'items'} in your cart
            </p>
          </div>
          <button onClick={()=>{clearCart();toast.info('Cart cleared.');}}
            className="text-sm font-semibold text-gray-400 hover:text-red-600
              transition-colors px-3 py-1.5 rounded-xl hover:bg-red-50">
            Clear all
          </button>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Items */}
          <div className="lg:col-span-2 bg-white rounded-3xl shadow-[0_2px_16px_rgba(0,0,0,0.06)] p-6 border border-gray-100/60">
            {items.map(item => <CartItem key={item.product.id} item={item}/>)}
          </div>

          {/* Summary */}
          <div className="space-y-4">
            <div className="bg-white rounded-3xl shadow-[0_2px_16px_rgba(0,0,0,0.06)] p-6 border border-gray-100/60">
              <p className="font-black text-gray-800 mb-4 text-sm">Have a coupon code?</p>
              <CouponInput/>
            </div>

            <div className="bg-white rounded-3xl shadow-[0_2px_16px_rgba(0,0,0,0.06)] p-6 border border-gray-100/60">
              <h3 className="font-black text-gray-800 text-lg mb-5"
                style={{fontFamily:'"Playfair Display",serif'}}>
                Order Summary
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between text-gray-500">
                  <span>Subtotal ({itemCount} items)</span>
                  <span className="font-bold text-gray-700">₹{subtotal.toFixed(0)}</span>
                </div>
                {coupon && (
                  <div className={`flex justify-between font-bold animate-fade-in
                    ${couponInvalid ? 'text-amber-500' : 'text-green-600'}`}>
                    <span className="flex items-center gap-1">
                      {couponInvalid ? '⚠' : ''} Coupon ({coupon?.code})
                    </span>
                    <span>{couponInvalid ? 'Paused' : `− ₹${discountAmount.toFixed(0)}`}</span>
                  </div>
                )}
                <div className="flex justify-between text-gray-500 items-start">
                  <span>Shipping</span>
                  <span className="text-amber-600 font-semibold text-xs text-right max-w-[160px]">
                    Calculated at confirmation
                  </span>
                </div>
                {/* Divider with gold accent */}
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex justify-between items-center">
                    <span className="font-black text-gray-900 text-base">Total Payable</span>
                    <span className="font-black text-3xl text-red-700"
                      style={{fontFamily:'"Plus Jakarta Sans",sans-serif', letterSpacing:'-0.02em'}}>
                      ₹{total.toFixed(0)}
                    </span>
                  </div>
                  {discountAmount>0 && !couponInvalid && (
                    <p className="text-xs text-green-600 font-medium mt-1 text-right">
                      🎉 You're saving ₹{discountAmount.toFixed(0)}!
                    </p>
                  )}
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200/60 rounded-xl px-3 py-2 mt-3 leading-relaxed">
                    🚚 Shipping charges are extra and will be communicated during order confirmation.
                  </p>
                </div>
              </div>

              {isBelowMinOrder && (
                <div className="bg-red-50 border border-red-200 rounded-2xl px-4 py-3 mt-4">
                  <p className="text-red-600 font-bold text-sm">
                    ⚠️ Minimum order value is ₹2,500
                  </p>
                  <p className="text-red-500 text-xs mt-1">
                    Please add ₹{(MIN_ORDER_VALUE - subtotal).toFixed(0)} more to proceed with checkout.
                  </p>
                </div>
              )}

              <button onClick={()=>navigate('/checkout')}
                disabled={couponExcluded || isBelowMinOrder}
                title={
                  isBelowMinOrder
                    ? `Minimum order value is ₹2,500. Add ₹${(MIN_ORDER_VALUE - subtotal).toFixed(0)} more.`
                    : couponExcluded
                    ? 'This coupon is not applicable for some items in your cart'
                    : ''
                }
                className={`group relative w-full mt-6 bg-gradient-to-br from-red-600 to-red-800
                  text-white font-black py-4 rounded-2xl text-base
                  shadow-xl shadow-red-600/30 hover:shadow-red-600/50
                  hover:-translate-y-0.5 active:scale-95 transition-all duration-300 overflow-hidden
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0`}>
                <span className="absolute inset-0 translate-x-[-100%] group-hover:translate-x-[100%]
                  transition-transform duration-700 bg-gradient-to-r from-transparent via-white/15 to-transparent skew-x-12"/>
                Proceed to Checkout →
              </button>

              <Link to="/products"
                className="block text-center text-xs text-gray-400 hover:text-red-600 mt-4 transition-colors">
                ← Continue Shopping
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
